import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import FormGroup from '@mui/material/FormGroup';
import { useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Toolbar from '@mui/material/Toolbar';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import useApi from '../hooks/useApi';
import CenteredProgress from '../loadingOverlay/CenteredProgress';
import Divider from '@mui/material/Divider';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import DatePicker from '../DatePicker';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import useSnackbar from '../hooks/useSnackbar';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import ConfirmDeleteImageDialog from './components/ConfirmDeleteImageDialog';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ConfirmDeleteImagesDialog from './components/ConfirmDeleteImagesDialog';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useParams, useLocation } from 'react-router';
import config from '../config';
import { format } from 'date-fns';
import buildUrl from '../buildUrl';
import Tooltip from '@mui/material/Tooltip';
import FailedImageDialog from './components/FailedImageDialog';
import formatFileSize from '../formatFileSize';
import CustomImageTablePagination from './components/CustomImageTablePagination';
import Stack from '@mui/material/Stack';

const rowsPerPageOptions = [10, 30, 50];

const columns = [
    { name: 'name', label: 'Name', align: 'left', sortable: true, dataType: 'text', width: '40%' },
    { name: 'type', label: 'Type', align: 'left', sortable: false },
    { name: 'created', label: 'Created', align: 'right', sortable: true, dataType: 'dateTime', width: '15%' },
    { name: 'size', label: 'Size', align: 'right', sortable: true, dataType: 'filesize', width: '15%' },
    { name: 'dimensions', label: 'Dimensions', align: 'right', sortable: false, width: '15%' }
];

const ImageManagement = ({ dialogMode = false, open = true, onClose, onSelect }) => {
    const theme = useTheme();
    const [files, setFiles] = useState([]);
    const { handleGet, handleFetch, handleDelete } = useApi();
    const { showSnackbar } = useSnackbar();
    const [selectedRows, setSelectedRows] = useState([]);
    const [isImageUploading, setIsImageUploading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageDimensions, setImageDimensions] = useState(null);
    const [imagesToDeleteDialog, setImagesToDeleteDialog] = useState(false);
    const [orderBy, setOrderBy] = useState('created');
    const [orderDesc, setOrderDesc] = useState(true);
    const [page, setPage] = useState(0);
    const [isInitialising, setIsInitialising] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isTabChanging, setIsTabChanging] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
    const [imagesCount, setImagesCount] = useState(0);
    const [unfilteredImages, setUnfilteredImages] = useState(null);
    const [images, setImages] = useState(null);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [view, setView] = useState('list');
    const { id, templateId } = useParams();
    const location = useLocation();
    const { PIXABAY_API_KEY, PEXELS_API_KEY } = config;
    const [contentInformation, setContentInformation] = useState(null);
    const [freeSearchValue, setFreeSearchValue] = useState('');
    const [activeFreeQuery, setActiveFreeQuery] = useState('');
    const [showFailedDialog, setShowFailedDialog] = useState(false);
    const [failedUploads, setFailedUploads] = useState([]);
    const tabsRef = useRef(null);
    const [tabsHeight, setTabsHeight] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);

    const handleScroll = (e) => {
        setIsScrolled(e.target.scrollTop > 0);
    };

    const [tabValue, setTabValue] = useState(() => {
        if (id && location.pathname.includes('/email')) {
            return 'email';
        }

        if (id && location.pathname.includes('/page')) {
            return 'page';
        }

        if (templateId && location.pathname.includes('/superadmin/templates')) {
            return 'template';
        }

        return 'account';
    });

    const handleViewChange = (event, nextView) => {
        if (nextView !== null) {
            setView(nextView);
        }
    };

    const handleRowSelection = (event, imageName) => {
        event.stopPropagation();

        const selectedIndex = selectedRows.indexOf(imageName);
        let newSelectedRows = [];

        if (selectedIndex === -1) {
            newSelectedRows = newSelectedRows.concat(selectedRows, imageName);
        }
        else if (selectedIndex === 0) {
            newSelectedRows = newSelectedRows.concat(selectedRows.slice(1));
        }
        else if (selectedIndex === selectedRows.length - 1) {
            newSelectedRows = newSelectedRows.concat(selectedRows.slice(0, -1));
        }
        else if (selectedIndex > 0) {
            newSelectedRows = newSelectedRows.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1)
            );
        }

        setSelectedRows(newSelectedRows);
    };

    const handleGetImages = async (tab = 'account') => {
        setIsInitialising(true);

        if (dialogMode && !tab) {
            return;
        }

        const baseUrl = `files/images/${tab}`;
        let params = {};

        if (dialogMode) {
            if (tab === 'template' && (location.pathname.includes('/email') || location.pathname.includes('/page'))) {
                params.id = contentInformation?.templateId;
            }
            else if (tab === 'template') {
                params.id = templateId;
            }
            else if (tab === 'email' || tab === 'page') {
                params.id = id;
            }
        }

        const url = buildUrl(baseUrl, Object.keys(params).length ? params : null);

        const response = await handleGet(url);

        if (!response.ok) {
            showSnackbar('Failed to load images', 'error');
            setIsLoading(false);
            return;
        }

        const data = await response.json();

        setUnfilteredImages(data);
        setIsLoading(false);
        setIsInitialising(false);
    };

    const handleGetImageDimensions = (event, image) => {
        const { naturalWidth, naturalHeight } = event.target;
        setImageDimensions(prevState => ({
            ...prevState,
            [image.name]: { width: naturalWidth, height: naturalHeight }
        }));
    };

    const handleUpload = async () => {
        setIsImageUploading(true);

        const results = [];
        const failures = [];
        const tab = dialogMode ? tabValue : 'account';

        for (let i = 0; i < files.length; i++) {
            const data = new FormData();
            data.append('file', files[i]);

            const baseUrl = 'files/images';
            const params = {};

            if (dialogMode && tab) {
                params.tab = tab;

                if ((tab === 'email' || tab === 'page') && id) {
                    params.id = id;
                }
                else if (tab === 'template' && templateId) {
                    params.id = templateId;
                }
            }

            const url = buildUrl(baseUrl, Object.keys(params).length ? params : null);

            const response = await handleFetch(url, {
                method: 'post',
                body: data
            });

            const result = await response.json();

            if (!response.ok) {
                const errorMessage = result?.result || 'Upload failed';
                failures.push({ name: files[i].name, error: errorMessage });
                continue;
            }

            results.push(result);
        }

        setFiles([]);
        setIsImageUploading(false);
        setIsLoading(false);
        handleGetImages(tab);

        if (results.length === 1) {
            onSelect(results[0]);
        }

        showSnackbar(`${results.length} ${results.length === 1 ? 'image' : 'images'} uploaded successfully`, 'success');

        if (failures.length > 0) {
            setFailedUploads(failures);
            setShowFailedDialog(true);
        }
    };

    const handleConfirmDeleteImage = (e, image) => {
        e.stopPropagation();
        setImageToDelete(image);
    };

    const handleDeleteImage = async (name) => {
        setIsLoading(true);
        const tab = dialogMode ? tabValue : 'account';
        const queryParams = [];

        let url = `files/images/${tab}`;

        if ((tab === 'email' || tab === 'page') && id) {
            queryParams.push(`id=${id}`);
        }

        if (tab === 'template' && templateId) {
            queryParams.push(`id=${templateId}`);
        }

        url += `/${name}`;

        if (queryParams.length > 0) {
            url += `?${queryParams.join('&')}`;
        }

        const response = await handleDelete(url);

        if (!response.ok) {
            console.error(response.error);
            setIsLoading(false);
            return;
        }

        setSelectedRows([]);
        setIsLoading(false);
        showSnackbar('Image deleted successfully', 'success');

        handleGetImages(tab);
    };

    const handleDeleteImages = async () => {
        setIsLoading(true);
        const imageLength = selectedRows.length;
        let count = 0;

        const tab = dialogMode ? tabValue : 'account';

        if (selectedRows.length > 0) {
            for (const row of selectedRows) {
                try {
                    let url = `files/images/${tab}`;
                    const queryParams = [];

                    if ((tab === 'email' || tab === 'page') && id) {
                        queryParams.push(`id=${id}`);
                    }

                    if (tab === 'template' && templateId) {
                        queryParams.push(`id=${templateId}`);
                    }

                    url += `/${row}`;

                    if (queryParams.length > 0) {
                        url += `?${queryParams.join('&')}`;
                    }

                    const response = await handleDelete(url);
                    count += 1;

                    if (!response.ok) {
                        console.error(response.error);
                        continue;
                    }

                    showSnackbar(`Image ${count} of ${imageLength} deleted successfully`, 'success');
                }
                catch (error) {
                    showSnackbar('Error deleting image', 'error');
                    continue;
                }
            }
        }

        setSelectedRows([]);
        setIsLoading(false);
        handleGetImages(tab);
    };

    const handleSort = async (orderByValue) => {
        if (orderBy === orderByValue) {
            setOrderDesc(!orderDesc);
        }
        else {
            setOrderDesc(orderBy);
        }

        setOrderBy(orderByValue);
    };

    const maxSize = 20 * 1024 * 1024;

    const onDrop = async (acceptedFiles, fileRejections) => {
        setIsLoading(true);

        const invalidFiles = acceptedFiles.filter(
            (file) => !file.type.startsWith('image/') || file.size > maxSize
        );

        if (invalidFiles.length > 0) {
            showSnackbar('Invalid File or size greater than 20mb', 'error');
        }
        else if (fileRejections.length > 0) {
            showSnackbar('File type not supported', 'error');
        }
        else {
            setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
        }

        setIsLoading(false);
    };

    const removeImage = (index, e) => {
        e.stopPropagation();
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const fetchFreeImages = async (query) => {
        setIsInitialising(true);

        const trimmedQuery = query?.trim();
        const perSource = Math.floor(rowsPerPage / 2);

        const useDefaultQuery = !trimmedQuery;

        try {
            const pexelsUrl = useDefaultQuery
                ? `https://api.pexels.com/v1/curated?per_page=${perSource}&page=${page + 1}`
                : `https://api.pexels.com/v1/search?query=${encodeURIComponent(trimmedQuery)}&per_page=${perSource}&page=${page + 1}`;

            const pixabayUrl = useDefaultQuery
                ? `https://pixabay.com/api/?key=${PIXABAY_API_KEY}&image_type=photo&per_page=${perSource}&page=${page + 1}`
                : `https://pixabay.com/api/?key=${PIXABAY_API_KEY}&q=${encodeURIComponent(trimmedQuery)}&image_type=photo&per_page=${perSource}&page=${page + 1}`;

            // eslint-disable-next-line no-undef
            const [pixabayRes, pexelsRes] = await Promise.all([
                fetch(pixabayUrl).then(res => res.json()),
                fetch(pexelsUrl, {
                    headers: { Authorization: PEXELS_API_KEY }
                }).then(res => res.json())
            ]);

            const pixabayImages = pixabayRes?.hits?.map(img => ({
                name: `pixabay-${img.id}`,
                link: img.webformatURL,
                size: img.imageSize,
                width: img.imageWidth,
                height: img.imageHeight
            })) || [];

            const pexelsImages = pexelsRes?.photos?.map(img => ({
                name: `pexels-${img.id}`,
                link: img.src?.medium || img.src?.original,
                size: null,
                width: img.width,
                height: img.height
            })) || [];

            const combinedImages = [...pixabayImages, ...pexelsImages];

            setImagesCount((pixabayRes?.total || 0) + (pexelsRes?.total_results || 0));
            setImages(combinedImages);
        }
        catch (error) {
            showSnackbar('Error fetching images', 'error');
            setImages([]);
            setUnfilteredImages([]);
            setImagesCount(0);
        }
        finally {
            setIsInitialising(false);
            setIsLoading(false);
        }
    };

    const handleChangeTab = async (event, newValue) => {
        setIsTabChanging(true);
        setTabValue(newValue);

        if (newValue === 'free') {
            setSearchValue('');
            setActiveFreeQuery('');
            setFromDate(null);
            setToDate(null);
            setPage(0);

            await fetchFreeImages('');
        }
        else {
            await handleGetImages(newValue);
        }

        setIsTabChanging(false);

    };

    const CircularSpinnerOverlay = () => (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress color="primary" />
        </Box>
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: ['image/jpeg', 'image/JPEG', 'image/jpg', 'image/JPG', 'image/png', 'image/PNG', 'image/gif', 'image/GIF'],
        onDrop
    });

    const handleInit = async () => {
        const selectedTab = dialogMode ? tabValue : 'account';

        await handleGetImages(selectedTab);
    };

    const handleGetContentInformation = async () => {
        if (!id) {
            return;
        }

        setIsInitialising(true);

        const pathname = location.pathname;
        let path = '';
        let label = '';

        if (pathname.includes('/email')) {
            path = pathname.includes('superadmin') ? 'superadmin/emails' : 'emails';
            label = 'email';
        }
        else if (pathname.includes('/page')) {
            path = 'pages';
            label = 'page';
        }
        else {
            setIsInitialising(false);
            return;
        }

        try {
            const response = await handleGet(`${path}/${id}`);

            if (!response.ok) {
                showSnackbar(`Failed to fetch ${label}`, 'error');
                return;
            }

            const data = await response.json();
            setContentInformation(data);
        }
        catch (err) {
            showSnackbar(`Failed to fetch ${label}`, 'error');
        }
        finally {
            setIsInitialising(false);
        }
    };

    const nonThumbnailImages = images?.filter(image => !image?.name.includes('stripothumbnailurl'));
    const allSelected = selectedRows.length === nonThumbnailImages?.length;
    const someSelected = selectedRows.length > 0;
    const toggleLabel = allSelected ? 'Deselect All' : someSelected ? 'Deselect' : 'Select All';

    const handleToggleSelection = () => {
        if (allSelected || someSelected) {
            setSelectedRows([]);
        }
        else {
            const imageNames = nonThumbnailImages?.map(image => image.name);
            setSelectedRows(imageNames);
        }
    };

    useEffect(() => {
        setPage(0);
    }, [searchValue, freeSearchValue, fromDate, toDate, orderBy, orderDesc, rowsPerPage]);

    useEffect(() => {
        handleInit();
    }, []);

    useEffect(() => {
        if (tabValue === 'free') {
            fetchFreeImages(activeFreeQuery);
        }
    }, [rowsPerPage, page, activeFreeQuery]);

    useEffect(() => {
        if (dialogMode && (location.pathname.includes('/email') || location.pathname.includes('/page'))) {
            handleGetContentInformation();
        }
    }, [dialogMode, location.pathname]);

    useEffect(() => {
        if (tabValue !== 'free') {
            let filteredDocs = unfilteredImages || [];

            if (searchValue) {
                filteredDocs = unfilteredImages.filter(e => e.name.toLowerCase().includes(searchValue.toLowerCase()));
            }

            const from = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : new Date('1970/01/01');
            const to = toDate ? new Date(toDate).setHours(23, 59, 59, 999) : new Date();

            if (toDate || fromDate) {
                filteredDocs = filteredDocs.filter(e => (new Date(e.created) >= from && new Date(e.created) <= to));
            }

            if (orderBy === 'name') {
                filteredDocs.sort((a, b) => {
                    const nameA = a.name.toLowerCase();
                    const nameB = b.name.toLowerCase();
                    return orderDesc ? nameB.localeCompare(nameA) : nameA.localeCompare(nameB);
                });
            }

            if (orderBy === 'size') {
                filteredDocs.sort((a, b) => orderDesc ? b.size - a.size : a.size - b.size);
            }

            if (orderBy === 'created') {
                filteredDocs.sort((a, b) =>
                    orderDesc ? new Date(b.created) - new Date(a.created) : new Date(a.created) - new Date(b.created)
                );
            }

            const count = filteredDocs.length;
            let newPage = page;

            const totalPages = Math.max(1, Math.ceil(count / rowsPerPage));

            if (newPage >= totalPages - 1) {
                newPage = totalPages - 1;
            }

            const start = newPage * rowsPerPage;
            const docs = filteredDocs.slice(start, start + rowsPerPage);

            setImages(docs);
            setImagesCount(count);

            if (newPage !== page) {
                setPage(newPage);
            }

            if (images) {
                setIsInitialising(false);
            }
        }
    }, [
        orderBy,
        orderDesc,
        page,
        isInitialising,
        searchValue,
        rowsPerPage,
        unfilteredImages,
        fromDate,
        toDate
    ]);

    useEffect(() => {
        return () => files.forEach(file => URL.revokeObjectURL(file.preview));
    }, []);

    useEffect(() => {
        const updateTabsHeight = () => {
            if (tabsRef.current) {
                setTabsHeight(tabsRef.current.offsetHeight || 0);
            }
        };

        updateTabsHeight();
        window.addEventListener('resize', updateTabsHeight);
        return () => window.removeEventListener('resize', updateTabsHeight);
    }, []);

    useEffect(() => {
        if (tabsRef.current) {
            setTabsHeight(tabsRef.current.offsetHeight || 0);
        }
    }, [tabValue]);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    if (!open) {
        return null;
    }

    const content = (
        <Paper id="imageLibrary" sx={{ height: dialogMode ? '80vh' : 'auto', overflowY: 'hidden', p: 0 }}>
            <Box id="imageScrollArea" onScroll={handleScroll} sx={{ height: dialogMode ? '80vh' : 'auto', overflowY: 'auto' }}>
                <Box>
                    {dialogMode && (
                        <Box
                            ref={tabsRef}
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: theme.zIndex.appBar,
                                bgcolor: theme.palette.background.paper,
                                display: 'flex',
                                p: 2
                            }}
                        >
                            <Tabs
                                value={tabValue}
                                onChange={handleChangeTab}
                                variant="standard"
                                TabIndicatorProps={{ style: { display: 'none' } }}
                                sx={{
                                    minHeight: 'unset',
                                    backgroundColor: theme.palette.grey[200],
                                    borderRadius: 2,
                                    p: 0.7,
                                    '& .MuiTabs-flexContainer': {
                                        gap: 2
                                    },
                                    '& .MuiTab-root': {
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        minHeight: 'unset',
                                        px: 2.5,
                                        py: 1.5,
                                        fontWeight: 650,
                                        fontSize: '0.875rem',
                                        color: theme.palette.text.secondary
                                    },
                                    '& .Mui-selected': {
                                        backgroundColor: theme.palette.background.paper,
                                        color: theme.palette.text.primary
                                    }
                                }}
                            >
                                <Tab value="account" label="Account" />
                                {location.pathname.includes('/email') && (
                                    <Tab value="email" label="Email" />
                                )}
                                {location.pathname.includes('/page') && (
                                    <Tab value="page" label="Page" />
                                )}
                                {(location.pathname.includes('/superadmin/templates') || ((location.pathname.includes('/email') || location.pathname.includes('/page')) && contentInformation?.templateId)) && (
                                    <Tab value="template" label="Template" />
                                )}
                                <Tab value="free" label="Free" />
                            </Tabs>
                            {tabValue !== 'free' && files && files.length > 0 && dialogMode && (
                                <Button sx={{ marginLeft: 'auto', mt: 2 }} variant="contained" onClick={handleUpload} disabled={isImageUploading}>Upload</Button>
                            )}
                        </Box>
                    )}
                    {tabValue !== 'free' && files && files.length > 0 && !dialogMode && (
                        <Button sx={{ marginLeft: 'auto', marginBottom: 2, display: 'flex', mr: 2 }} variant="contained" onClick={handleUpload} disabled={isImageUploading}>Upload</Button>
                    )}
                    {tabValue !== 'free' && (tabValue !== 'template' || location.pathname.includes('/superadmin/templates')) && (
                        <FormGroup>
                            <Box sx={{ width: '100%', px: 2 }}>
                                <Box
                                    {...getRootProps()}
                                    sx={{
                                        border: `2px dashed ${theme.palette.primary.light}`,
                                        borderRadius: 2,
                                        p: 3,
                                        backgroundColor: isDragActive
                                            ? theme.palette.primary.lighter || theme.palette.action.hover
                                            : theme.palette.background.default,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s ease',
                                        minHeight: 150
                                    }}
                                >
                                    <input {...getInputProps({ multiple: true })} />

                                    {files.length === 0 && (
                                        <Stack
                                            textAlign="center"
                                            alignItems="center"
                                            justifyContent="center"
                                            spacing={1}
                                        >
                                            <CloudUploadIcon fontSize="large" color="primary" />
                                            <Typography variant="subtitle1">
                                                Drop images here or click to browse
                                            </Typography>
                                            <Typography variant="button" color="text.secondary">
                                                .png, .jpg, .jpeg, .gif (max 20 MB)
                                            </Typography>
                                        </Stack>
                                    )}
                                    {files.length > 0 && (
                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                            {files.map((file, index) => (
                                                <Grid key={file.name}>
                                                    <Paper
                                                        sx={{
                                                            width: 100,
                                                            height: 100,
                                                            borderRadius: 2,
                                                            overflow: 'hidden',
                                                            position: 'relative',
                                                            boxShadow: 1,
                                                            border: `1px solid ${theme.palette.divider}`
                                                        }}
                                                    >
                                                        <img
                                                            src={URL.createObjectURL(file)}
                                                            alt={file.name}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                        {isImageUploading && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    bgcolor: theme.palette.background.paper,
                                                                    opacity: 0.8,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <CircularSpinnerOverlay />
                                                            </Box>
                                                        )}
                                                        {!isImageUploading && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => removeImage(index, e)}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 4,
                                                                    right: 4,
                                                                    backgroundColor: theme.palette.background.paper,
                                                                    color: theme.palette.text.primary,
                                                                    '&:hover': {
                                                                        backgroundColor: theme.palette.grey[200]
                                                                    }
                                                                }}
                                                            >
                                                                <ClearIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Paper>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            mt: 0.5,
                                                            display: 'block',
                                                            textAlign: 'center',
                                                            maxWidth: 100,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {file.name.split('.').slice(0, -1).join('.')}
                                                    </Typography>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Box>
                            </Box>
                        </FormGroup>
                    )}
                    <Box
                        mt={
                            tabValue !== 'free' &&
                                (tabValue !== 'template' || location.pathname.includes('/superadmin/templates'))
                                ? 2
                                : 0.5
                        }
                        px={2}
                        pb={2}
                    >
                        {isInitialising || isTabChanging ? <CenteredProgress /> : (
                            <Paper>
                                {tabValue !== 'free' ? (
                                    <Box
                                        sx={{
                                            position: 'sticky',
                                            top: tabsHeight,
                                            zIndex: theme.zIndex.appBar - 1,
                                            bgcolor: theme.palette.background.paper,
                                            borderBottom: isScrolled ? `1px solid ${theme.palette.divider}` : null
                                        }}
                                    >
                                        <Toolbar disableGutters sx={{ mx: 1 }}>
                                            <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', flexWrap: 'wrap', gap: 1, mr: 2 }}>
                                                <OutlinedInput
                                                    value={searchValue}
                                                    onChange={e => setSearchValue(e.target.value)}
                                                    placeholder="Search..."
                                                    size="small"
                                                    sx={{ minWidth: 180 }}
                                                    endAdornment={(
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setSearchValue('')}
                                                                edge="end"
                                                                disabled={!Boolean(searchValue)}
                                                            >
                                                                <ClearIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )}
                                                />
                                                <DatePicker value={fromDate} onChange={date => setFromDate(date)} label="Created from date" />
                                                <DatePicker value={toDate} onChange={date => setToDate(date)} label="Created to date" />
                                                <ToggleButtonGroup
                                                    value={view}
                                                    exclusive
                                                    onChange={handleViewChange}
                                                    size="small"
                                                >
                                                    <ToggleButton value="list" sx={{ px: 1.5 }}>
                                                        <ViewListIcon />
                                                    </ToggleButton>
                                                    <ToggleButton value="grid" sx={{ px: 1.5 }}>
                                                        <ViewModuleIcon />
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            </Box>
                                            {view === 'grid' && (
                                                <>
                                                    <Button
                                                        onClick={handleToggleSelection}
                                                        sx={{ whiteSpace: 'nowrap', ml: 2 }}
                                                        variant="outlined"
                                                        size="small"
                                                    >
                                                        {toggleLabel}
                                                    </Button>
                                                </>
                                            )}
                                            {!(tabValue === 'template' && location.pathname.includes('/editor')) && (
                                                <Button
                                                    onClick={() => setImagesToDeleteDialog(true)}
                                                    sx={{ whiteSpace: 'nowrap', ml: 2 }}
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={selectedRows.length === 0}
                                                    color="error"
                                                >
                                                    Delete {selectedRows.length > 0 ? `(${selectedRows.length})` : ''}
                                                </Button>
                                            )}
                                        </Toolbar>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            position: 'sticky',
                                            top: tabsHeight,
                                            zIndex: theme.zIndex.appBar - 1,
                                            bgcolor: theme.palette.background.paper,
                                            borderBottom: isScrolled ? `1px solid ${theme.palette.divider}` : null
                                        }}
                                    >
                                        <Toolbar disableGutters sx={{ mx: 1 }}>
                                            <OutlinedInput
                                                value={freeSearchValue}
                                                onChange={e => setFreeSearchValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        setActiveFreeQuery(freeSearchValue);
                                                        fetchFreeImages(freeSearchValue);
                                                    }
                                                }}
                                                placeholder="Search free images..."
                                                size="small"
                                                sx={{ minWidth: 250 }}
                                                endAdornment={(
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => {
                                                                setFreeSearchValue('');
                                                            }}
                                                            edge="end"
                                                            disabled={!Boolean(freeSearchValue)}
                                                        >
                                                            <ClearIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )}
                                            />
                                        </Toolbar>
                                    </Box>
                                )}
                                <Divider />
                                {tabValue !== 'free' && view === 'list' ? (
                                    images.filter(image => !image?.name.includes('stripothumbnailurl')).length > 0 ? (
                                        <>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell padding="checkbox">
                                                                <Checkbox
                                                                    indeterminate={
                                                                        selectedRows.length > 0 &&
                                                                        selectedRows.length < images.filter(image => !image?.name.includes('stripothumbnailurl')).length
                                                                    }
                                                                    checked={
                                                                        selectedRows.length > 0 &&
                                                                        selectedRows.length === images.filter(image => !image?.name.includes('stripothumbnailurl')).length
                                                                    }
                                                                    onChange={(e) => {
                                                                        const filtered = images
                                                                            .filter(image => !image?.name.includes('stripothumbnailurl'))
                                                                            .map(image => image.name);

                                                                        setSelectedRows(e.target.checked ? filtered : []);
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            {columns.map((col) => (
                                                                <TableCell
                                                                    variant="head"
                                                                    key={col.name}
                                                                    align={col.align || 'left'}
                                                                    width={col.width}
                                                                >
                                                                    {col.sortable ? (
                                                                        <TableSortLabel
                                                                            active={orderBy === col.name}
                                                                            direction={orderDesc ? 'desc' : 'asc'}
                                                                            onClick={() => handleSort(col.name)}
                                                                        >
                                                                            {col.label}
                                                                        </TableSortLabel>
                                                                    ) : (
                                                                        col.label
                                                                    )}
                                                                </TableCell>
                                                            ))}
                                                            <TableCell sx={{ width: '4%' }} />
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {images
                                                            .filter(image => !image?.name.includes('stripothumbnailurl'))
                                                            .map(image => (
                                                                <TableRow key={image.name}>
                                                                    <TableCell padding="checkbox">
                                                                        <Checkbox
                                                                            checked={selectedRows.includes(image.name)}
                                                                            onClick={event => handleRowSelection(event, image.name)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell sx={{ maxWidth: 360, paddingRight: 1 }}>
                                                                        <Box sx={{ display: 'flex', gap: 2, minWidth: 0, alignItems: 'center' }}>
                                                                            <Box
                                                                                sx={{
                                                                                    width: 40,
                                                                                    height: 40,
                                                                                    borderRadius: 1,
                                                                                    border: '1px solid #ddd',
                                                                                    cursor: 'pointer',
                                                                                    flex: '0 0 auto',
                                                                                    overflow: 'hidden',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center'
                                                                                }}
                                                                                onClick={() => setSelectedImage(image.link)}
                                                                            >
                                                                                <img
                                                                                    src={image.link}
                                                                                    alt={image.name}
                                                                                    onLoad={(e) => handleGetImageDimensions(e, image)}
                                                                                    style={{
                                                                                        maxWidth: '100%',
                                                                                        maxHeight: '100%',
                                                                                        borderRadius: 1,
                                                                                        objectFit: 'contain',
                                                                                        backgroundImage: 'url("/images/trans-bg-rpt.png")',
                                                                                        backgroundSize: 'contain'
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    minWidth: 0,
                                                                                    flex: 1,
                                                                                    wordBreak: 'break-word',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center'
                                                                                }}
                                                                            >
                                                                                {image.name.split('.').slice(0, -1).join('.')}
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableCell>

                                                                    <TableCell>
                                                                        <Typography sx={{ verticalAlign: 'middle' }} variant="body2" color="text.secondary">
                                                                            {image.name.split('.').pop().toUpperCase()}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {image.created ? format(new Date(image.created), 'dd/MM/yyyy HH:mm') : '-'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {formatFileSize(image.size)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {imageDimensions?.[image.name]
                                                                                ? `${imageDimensions[image.name].width} × ${imageDimensions[image.name].height}`
                                                                                : '-'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ pr: 0 }}>
                                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                                            {!(tabValue === 'template' && location.pathname.includes('/editor')) && (
                                                                                <IconButton
                                                                                    onClick={e => handleConfirmDeleteImage(e, image)}
                                                                                    size="small"
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            )}
                                                                            {onSelect && (
                                                                                <Button
                                                                                    variant="outlined"
                                                                                    size="small"
                                                                                    onClick={() => onSelect(image)}
                                                                                    sx={{ mr: 1 }}
                                                                                >
                                                                                    Select
                                                                                </Button>
                                                                            )}
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            {dialogMode ? (
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="flex-end"
                                                    gap={2}
                                                >
                                                    <Typography sx={{ mt: 1 }} variant="body2" gutterBottom> Rows per page:</Typography>
                                                    <CustomImageTablePagination
                                                        rowsPerPage={rowsPerPage}
                                                        setRowsPerPage={setRowsPerPage}
                                                        setPage={setPage}
                                                        options={rowsPerPageOptions}
                                                    />
                                                    <TablePagination
                                                        component="div"
                                                        count={imagesCount}
                                                        rowsPerPage={rowsPerPage}
                                                        page={page}
                                                        onPageChange={(e, newPage) => setPage(newPage)}
                                                        labelRowsPerPage={null}
                                                        rowsPerPageOptions={[]}
                                                    />
                                                </Box>
                                            ) : (
                                                <TablePagination
                                                    rowsPerPageOptions={rowsPerPageOptions}
                                                    component="div"
                                                    count={imagesCount}
                                                    rowsPerPage={rowsPerPage}
                                                    page={page}
                                                    onPageChange={(e, newPage) => setPage(newPage)}
                                                    onRowsPerPageChange={e => setRowsPerPage(e.target.value)}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <Box sx={{ py: 6, textAlign: 'center' }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No images
                                            </Typography>
                                        </Box>
                                    )
                                ) : (
                                    images.filter(image => !image?.name.includes('stripothumbnailurl')).length > 0 ? (
                                        <>
                                            <Grid container spacing={2} sx={{ px: 1, py: 2 }}>
                                                {images
                                                    .filter(image => !image?.name.includes('stripothumbnailurl'))
                                                    .map((image) => (
                                                        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={image.name}>
                                                            <Paper
                                                                variant="outlined"
                                                                sx={{
                                                                    position: 'relative',
                                                                    p: 1,
                                                                    textAlign: 'center',
                                                                    borderRadius: 2,
                                                                    cursor: 'pointer',
                                                                    '&:hover': { boxShadow: 3 }
                                                                }}
                                                                onClick={() => setSelectedImage(image.link)}
                                                            >
                                                                {tabValue !== 'free' && (
                                                                    <Checkbox
                                                                        checked={selectedRows.includes(image.name)}
                                                                        onClick={(e) => handleRowSelection(e, image.name)}
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: 8,
                                                                            left: 8,
                                                                            backgroundColor: 'background.paper',
                                                                            borderRadius: 1,
                                                                            '&:hover': {
                                                                                backgroundColor: 'background.paper'
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                                <Box
                                                                    sx={{
                                                                        width: '100%',
                                                                        height: 160,
                                                                        borderRadius: 1,
                                                                        border: '1px solid #ddd',
                                                                        cursor: 'pointer',
                                                                        flex: '0 0 auto',
                                                                        overflow: 'hidden',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        mb: 1
                                                                    }}
                                                                    onClick={() => setSelectedImage(image.link)}
                                                                >
                                                                    <img
                                                                        src={image.link}
                                                                        alt={image.name}
                                                                        onLoad={(e) => handleGetImageDimensions(e, image)}
                                                                        style={{
                                                                            maxWidth: '100%',
                                                                            maxHeight: '100%',
                                                                            borderRadius: 1,
                                                                            objectFit: 'contain',
                                                                            backgroundImage: 'url("/images/trans-bg-rpt.png")',
                                                                            backgroundSize: 'contain'
                                                                        }}
                                                                    />
                                                                </Box>
                                                                {tabValue !== 'free' && (
                                                                    <>
                                                                        <Tooltip title={image.name.split('.').slice(0, -1).join('.')} arrow placement="top">
                                                                            <Typography
                                                                                variant="body2"
                                                                                noWrap
                                                                                sx={{
                                                                                    maxWidth: '100%',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                }}
                                                                            >
                                                                                {image.name.split('.').slice(0, -1).join('.')}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {image.name.split('.').pop().toUpperCase()}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                                {onSelect && (
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        fullWidth
                                                                        sx={{ mt: 1 }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onSelect(image);
                                                                        }}
                                                                    >
                                                                        Select
                                                                    </Button>
                                                                )}
                                                            </Paper>
                                                        </Grid>
                                                    ))}
                                            </Grid>
                                            <Divider />
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="flex-end"
                                                gap={2}
                                            >
                                                {dialogMode ? (
                                                    <>
                                                        <Typography sx={{ mt: 1 }} variant="body2" gutterBottom> Rows per page:</Typography>
                                                        <CustomImageTablePagination
                                                            rowsPerPage={rowsPerPage}
                                                            setRowsPerPage={setRowsPerPage}
                                                            setPage={setPage}
                                                            options={rowsPerPageOptions}
                                                        />
                                                        <TablePagination
                                                            component="div"
                                                            count={imagesCount}
                                                            rowsPerPage={rowsPerPage}
                                                            page={page}
                                                            onPageChange={(e, newPage) => setPage(newPage)}
                                                            labelRowsPerPage={null}
                                                            rowsPerPageOptions={[]}
                                                        />
                                                    </>
                                                ) : (
                                                    <TablePagination
                                                        rowsPerPageOptions={rowsPerPageOptions}
                                                        component="div"
                                                        count={imagesCount}
                                                        rowsPerPage={rowsPerPage}
                                                        page={page}
                                                        onPageChange={(e, newPage) => setPage(newPage)}
                                                        onRowsPerPageChange={e => setRowsPerPage(e.target.value)}
                                                    />
                                                )}
                                            </Box>
                                        </>
                                    ) : (
                                        <Box sx={{ py: 6, textAlign: 'center' }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No images
                                            </Typography>
                                        </Box>
                                    )
                                )}
                            </Paper>
                        )}
                    </Box>
                    {imageToDelete && (
                        <ConfirmDeleteImageDialog
                            onClose={() => setImageToDelete(null)}
                            onDelete={handleDeleteImage}
                            imageName={imageToDelete.name}
                            image={imageToDelete}
                        />
                    )}
                    {imagesToDeleteDialog && (
                        <ConfirmDeleteImagesDialog
                            onClose={() => setImagesToDeleteDialog(false)}
                            onDelete={handleDeleteImages}
                            imageLength={selectedRows.length}
                        />
                    )}
                    {showFailedDialog && (
                        <FailedImageDialog
                            onClose={() => setShowFailedDialog(false)}
                            open={showFailedDialog}
                            failedUploads={failedUploads}
                        />
                    )}
                    <Modal open={selectedImage !== null}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }} onClick={() => setSelectedImage(null)}>
                            <img src={selectedImage} style={{ maxHeight: '90vh', maxWidth: '90vw', background: 'url(\'https://plugins.stripo.email/static/latest/assets/img/transp-back.png\')' }} alt="Full Size" />
                        </Box>
                    </Modal>
                </Box>
            </Box>
        </Paper>
    );

    return dialogMode ? (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
            id="imageManagementDialog"
        >
            <DialogTitle
                sx={{
                    m: 0,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                Image Library
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500]
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
                {content}
            </DialogContent>
        </Dialog>
    ) : content;
};

export default ImageManagement;